<template>
  <v-layout column justify-space-between class="manage-rulecollection white">
    <div>
      <!-- Title and Actions -->
      <v-flex xs12 class="py-0">
        <v-toolbar color="secondary" class="elevation-0">
          <!-- Back Button -->
          <v-btn icon flat class="primary--text text--lighten-5" @click="$router.go(-1)">
            <v-icon>arrow_back</v-icon>
          </v-btn>
          <v-toolbar-title class="white--text subheading ttu">
            <router-link :to="{ name: 'XVATRulesSearch' }" class="white--text link dim">{{ messages.label.METADATA_RULES
            }}</router-link>/
            <span v-if="isEditMode">{{ messages.label.EDIT_RULE_COLLECTION }} - "{{ currentRuleCollection.title
            }}"</span>
            <span v-else>{{ messages.label.CREATE_RULE_COLLECTION }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
      </v-flex>
      <!-- Main Content -->
      <v-flex xs12>
        <v-card flat>
          <!-- Header with tabs and actions -->
          <v-card-title class="grey lighten-4 px-3 py-0">
            <v-tabs v-model="activeTab" @change="ensureWindow" color="grey lighten-4">
              <v-tabs-slider color="accent"></v-tabs-slider>
              <v-tab v-for="item in tabItems" :key="item" class="secondary--text">{{ item }}</v-tab>
              <v-spacer></v-spacer>
            </v-tabs>
            <v-spacer></v-spacer>
            <v-btn color="primary" round @click="showTestRuleModal()" v-if="isEditMode"
              :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress">{{ messages.label.TEST }}
            </v-btn>
          </v-card-title>
          <!-- Main content of rule (info, conditions, actions) -->
          <v-card-text class="pt-1">
            <!-- Loading indicator -->
            <v-progress-linear color="accent" v-show="dataLoading" height="2" :indeterminate="true"></v-progress-linear>
            <!-- Error Alert -->
            <v-flex xs12 v-if="errorAlert">
              <v-alert v-model="errorAlert" dismissible type="error">{{ errorMessage }}</v-alert>
            </v-flex>
            <v-tabs-items v-model="activeTab" ref="window">
              <v-tab-item>
                <RuleCollectionInfo />
              </v-tab-item>
              <v-tab-item>
                <RuleCollectionRuleSets />
              </v-tab-item>
              <v-tab-item v-if="isEditMode">
                <JSONView :json="currentRuleCollectionJSON" />
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>
      </v-flex>
      <!-- Rule Test Dialog -->
      <XVATRuleTest v-model="ruleTestDialog" :rule-obj="ruleObjectToTest" :rule-category="'ruleCollection'" />
      <!-- Snackbar -->
      <v-flex xs12>
        <v-snackbar :bottom="true" :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
          {{ snackbar.message }}
          <v-btn flat color="secondary" @click.native="snackbar.show = false">{{ messages.label.OK }}</v-btn>
        </v-snackbar>
      </v-flex>
    </div>
    <!-- Card actions -->
    <v-card-actions class="grey lighten-4 px-3">
      <v-btn color="grey lighten-1 white--text" @click="cancel()">{{ messages.label.CANCEL }}</v-btn>
      <v-btn color="primary" v-if="isEditMode" :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress"
        @click="validateForUpdateRuleCollection()">{{ messages.label.SAVE }}</v-btn>
      <v-btn color="primary" v-else :disabled="createOrUpdateInProgress" :loading="createOrUpdateInProgress"
        @click="validateForCreateRuleCollection()">{{ messages.label.CREATE }}</v-btn>
      <v-spacer></v-spacer>
      <v-btn color="error" v-if="isEditMode" @click="showDeleteDialog()">
        <span>{{ messages.label.DELETE }}</span>
      </v-btn>
      <!-- Delete Dialog -->
      <v-dialog v-model="deleteDialog" width="500">
        <v-card>
          <v-card-title class="subheading ttu">{{ messages.label.DELETE }}</v-card-title>
          <v-card-text>
            <!-- Error Alert -->
            <v-flex xs12 v-if="deleteErrorAlert">
              <v-alert v-model="deleteErrorAlert" type="error">{{ deleteErrorMessage }}</v-alert>
            </v-flex>
            <p>
              {{ messages.info.DELETE_RULE_1 }}
              <b>{{ currentRuleCollection.title }}</b> ?
            </p>
            <p v-html="messages.info.DELETE_RULE_2"></p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey lighten-1 white--text" @click="cancelDelete">{{ messages.label.CANCEL }}</v-btn>
            <v-btn color="error" :disabled="deletingRule" :loading="deletingRule" @click="deleteRuleCollection()">{{
                messages.label.YES
            }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card-actions>
  </v-layout>
</template>

<script>
import { mapState, mapMutations } from "vuex"
import XVATRulesService from "@/XVAT/XVATRulesService"
import * as xvatDataService from "@/XVAT/XVATDataService"
import * as utils from "@/Shared/utils"
import AppService from "@/AppService"
import RuleQuickView from "@/XVAT/Rule/RuleQuickView"
import RuleCollectionRuleSets from "@/XVAT/RuleCollection/RuleCollectionRuleSets"
import RuleCollectionInfo from "@/XVAT/RuleCollection/RuleCollectionInfo"
import JSONView from "@/XVAT/JSONView"
import XVATRuleTest from "@/XVAT/XVATRuleTest"
import messages from "@/XVAT/messages.json"
import { SET_RULECOLLECTION_RULESETS } from "@/store/constants/mutation-types"

export default {
  name: "XVATManageRuleCollection",
  components: {
    RuleCollectionInfo,
    RuleCollectionRuleSets,
    RuleQuickView,
    JSONView,
    XVATRuleTest
  },
  data() {
    return {
      activeTab: null,
      isEditMode: false,
      errorAlert: false,
      errorMessage: "",
      ruleObjectToTest: {},
      ruleTestDialog: false,
      validationStatus: {
        info: false,
        rules: true
      },
      ruleCollectionAction: "",
      ruleCollectionInfoData: {},
      currentRuleCollection: {},
      currentRuleCollectionJSON: {},
      snackbar: {
        show: false,
        timeout: 5000,
        message: "",
        color: "error"
      },
      dataLoading: false,
      createOrUpdateInProgress: false,
      deleteDialog: false,
      deletingRule: false,
      deleteErrorAlert: false,
      deleteErrorMessage: "",
      windowTimeout: null
    }
  },
  watch: {
    validationStatus: {
      handler(newValue) {
        if (newValue.info === true && newValue.rules === true) {
          switch (this.ruleCollectionAction) {
            case "create":
              this.createRuleCollection()
              break
            case "update":
              this.updateRuleCollection()
              break
          }
        }
      },
      deep: true
    }
  },
  computed: {
    ...mapState("xvat", ["ruleCollectionRuleSetsList"]),
    tabItems() {
      if (this.isEditMode) {
        return ["INFO",
          "RULE SETS",
          "{ JSON }"]
      } else {
        return ["INFO",
          "RULE SETS"]
      }
    }
  },
  methods: {
    ...mapMutations("xvat", [
      SET_RULECOLLECTION_RULESETS
    ]),
    /**
     * validateForUpdateRuleCollection - Begin validation for update ruleset
     */
    validateForUpdateRuleCollection() {
      this.ruleCollectionAction = "update"
      this.validateAll()
    },

    /**
     * validateForCreateRuleCollection - Begin validation for create ruleset
     */
    validateForCreateRuleCollection() {
      this.ruleCollectionAction = "create"
      this.validateAll()
    },

    /**
     * resetValidationStatus - Reset validation status
     */
    resetValidationStatus() {
      this.validationStatus = {
        info: false,
        rules: false
      }
    },

    /**
     * resetData - Reset data
     */
    resetData() {
      if (this.$lodash.isNull(this.activeTab)) {
        this.activeTab = 0
      }
      this.isEditMode = false
      this.ruleCollectionAction = ""
      this.ruleCollectionInfoData = {}
      this.currentRuleCollection = {}
      this.currentRuleCollectionJSON = {}
      this.errorAlert = false
      this.errorMessage = ""
      this.SET_RULECOLLECTION_RULESETS([])
      this.resetValidationStatus()
    },

    /**
     * validateAll - Validate ruleset info and rules
     */
    validateAll() {
      XVATRulesService.$emit("event:validateRuleCollectionInfo")
      XVATRulesService.$emit("event:validateRuleCollectionRuleSets")
    },

    /**
     * validateRuleCollectionInfo - Handles validation of ruleset info view
     */
    validateRuleCollectionInfo(result, ruleCollectionInfoData) {
      this.validationStatus.info = result
      this.ruleCollectionInfoData = ruleCollectionInfoData
      if (!this.validationStatus.info) {
        this.activeTab = 0 // Show Info tab
      }
    },

    /**
     * validateRuleCollectionRuleSets - Handles validation of rules view
     */
    validateRuleCollectionRuleSets(result) {
      this.validationStatus.rules = result
      if (!this.validationStatus.rules) {
        this.activeTab = 1 // Show Info tab
      }
    },

    /**
     * createRuleCollection - Create new ruleset
     */
    createRuleCollection() {
      this.createOrUpdateInProgress = true
      this.ruleCollectionInfoData.title = this.$lodash.trim(this.ruleCollectionInfoData.title)
      this.ruleCollectionInfoData.description = this.$lodash.trim(this.ruleCollectionInfoData.description)

      let ruleCollectionData = {
        title: this.ruleCollectionInfoData.title,
        type: this.ruleCollectionInfoData.type,
        disabled: !this.ruleCollectionInfoData.enabled,
        description: encodeURI(this.ruleCollectionInfoData.description),
        inputItems: this.getRuleCollectionRuleSetIds(),
        locked: false,
        tags: []
      }
      xvatDataService
        .createRuleCollection(ruleCollectionData)
        .then((response) => {
          let ruleCollectionId = response.data.id
          this.showSnackBar("success", this.messages.info.RULECOLLECTION_CREATE_SUCCESS)
          this.$router.replace({
            name: "XVATManageRuleCollection",
            params: { id: ruleCollectionId }
          })
        })
        .catch((error) => {
          this.resetValidationStatus()
          this.showError(error)
        })
        .finally(() => {
          this.createOrUpdateInProgress = false
          XVATRulesService.$emit("event:refreshRulesList")
        })
    },

    /**
     * updateRuleCollection - Update existing ruleset
     */
    updateRuleCollection() {
      this.createOrUpdateInProgress = true
      // Update current rule data
      this.currentRuleCollection.title = this.$lodash.trim(this.ruleCollectionInfoData.title)
      this.currentRuleCollection.type = this.ruleCollectionInfoData.type
      this.currentRuleCollection.disabled = !this.ruleCollectionInfoData.enabled
      this.currentRuleCollection.description = this.$lodash.trim(this.ruleCollectionInfoData.description)
      this.currentRuleCollection.description = encodeURI(this.currentRuleCollection.description)
      this.currentRuleCollection.inputItems = this.getRuleCollectionRuleSetIds()
      //  TODO: Temp, this should not be required, discuss with Christian
      this.currentRuleCollection["locked"] = false
      //  TODO: Temp , discuss with Christian
      let ruleCollectionId = this.currentRuleCollection.id
      let updatedRuleCollection = this.$lodash.omit(this.currentRuleCollection, ["id", "added", "addedByUserId", "version", "updated", "updatedByUserId", "items"])

      xvatDataService
        .updateRuleCollection(ruleCollectionId,
          updatedRuleCollection)
        .then(() => {
          this.showSnackBar("success", this.messages.info.RULECOLLECTION_UPDATE_SUCCESS)
          let ruleCollectionId = this.currentRuleCollection.id
          this.loadRuleCollectionData(ruleCollectionId)
        })
        .catch((error) => {
          this.showError(error)
        })
        .finally(() => {
          this.resetValidationStatus()
          this.createOrUpdateInProgress = false
          XVATRulesService.$emit("event:refreshRulesList")
        })
    },

    /**
     * getRuleCollectionRuleSetIds - Retrieves just the rule ids required for create / update
     */
    getRuleCollectionRuleSetIds() {
      if (this.ruleCollectionRuleSetsList.length > 0) {
        return this.$lodash.map(this.ruleCollectionRuleSetsList, "id")
      } else {
        return []
      }
    },

    /**
     * retreiveRuleCollectionDetails - Retrieve rule collection details with given id
     * @param {string} ruleCollectionId
     */
    retreiveRuleCollectionDetails(ruleCollectionId) {
      this.currentRuleCollection = {}
      this.currentRuleCollectionJSON = {}

      return xvatDataService
        .getRuleCollection(ruleCollectionId)
        .then((response) => {
          // RuleSet data from "data" service
          if (response.data) {
            this.currentRuleCollection = response.data.entries[0]
            this.currentRuleCollectionJSON = this.$lodash.cloneDeep(this.currentRuleCollection)
            if (this.currentRuleCollection.items.length > 0) {
              let ruleCollectionRuleSets = XVATRulesService.addClientOnlyFields(this.currentRuleCollection.items)
              this.SET_RULECOLLECTION_RULESETS(ruleCollectionRuleSets)
            }
            return this.currentRuleCollection
          } else {
            // TODO: Redirect to 404
            return Promise.reject(
              new Error(`Could not find rule collection with id: ${ruleCollectionId}`)
            )
          }
        })
        .then((response) => {
          this.currentRuleCollectionJSON = this.$lodash.cloneDeep(response.data)
        })
        .catch((error) => {
          return Promise.reject(error)
        })
    },

    /**
     * loadRuleCollectionData - Takes care of loading rule collection data in the views
     * @param {string} ruleId
     * @param {string} account
     */
    loadRuleCollectionData(ruleCollectionId) {
      this.dataLoading = true
      this.resetData()
      if (!this.$lodash.isUndefined(ruleCollectionId)) {
        // Edit mode
        this.isEditMode = true
        return this.retreiveRuleCollectionDetails(ruleCollectionId)
          .then((response) => {
            // Rule collection data retrieved
            XVATRulesService.$emit("event:ruleCollectionDataRetrieved", this.currentRuleCollection)
            return response
          }).catch((error) => {
            this.showError(error)
          }).finally(() => {
            this.dataLoading = false
          })
      } else {
        // Create mode
        this.isEditMode = false
        XVATRulesService.$emit("event:ruleCollectionDataRetrieved")
        this.dataLoading = false
        return Promise.resolve({})
      }
    },

    /**
     * showSnackBar - Show snackbar message
     * @param {string} color
     * @param {string} message
     */
    showSnackBar(color, message) {
      this.snackbar.show = true
      this.snackbar.color = color
      this.snackbar.message = message
    },

    /**
     * showError - Handles displaying error
     * @param {object} error
     */
    showError(error) {
      let isServerError = utils.isMPXError(error)
      let isClientError = utils.isClientError(error)
      if (isServerError) {
        this.errorAlert = true
        this.errorMessage = error.response.data.description
      } else if (isClientError) {
        this.errorAlert = true
        this.errorMessage = error.message
      }
    },
    /**
     * cancel - Cancel out of rule create / edit view
     */
    cancel() {
      this.$router.push({ name: "XVATRulesSearch" })
    },
    /**
    * showDeleteDialog -
    */
    showDeleteDialog() {
      this.deleteDialog = true
    },

    /**
     * cancelDelete - close delete dialog
     */
    cancelDelete() {
      this.deleteErrorAlert = false
      this.deleteErrorMessage = ""
      this.deleteDialog = false
    },

    /**
     * deleteRuleCollection - Delete selected rule / ruleset / rule collection
     */
    deleteRuleCollection() {
      this.deletingRule = true
      let ruleCollectionId = this.currentRuleCollection.id
      xvatDataService
        .deleteRuleCollection(ruleCollectionId)
        .then(() => {
          this.deleteDialog = false
          // Show Snackbar
          this.snackbar.show = true
          this.snackbar.color = "success"
          this.snackbar.message = this.messages.info.DELETE_SUCCESS + this.currentRuleCollection.title
          // route user to search screen
          this.$router.push({ name: "XVATRulesSearch" })
        }).catch((error) => {
          let isServerError = utils.isMPXError(error)
          if (isServerError) {
            this.deleteErrorAlert = true
            this.deleteErrorMessage = error.response.data.description
          }
        })
        .finally(() => {
          this.deletingRule = false
          XVATRulesService.$emit("event:refreshRulesList")
        })
    },

    /**
     * showTestRuleModal
     */
    showTestRuleModal() {
      this.ruleObjectToTest = this.currentRuleCollection
      this.ruleTestDialog = true
    },

    /**
     * NOTE: Fix for auto height adjust issue of v-tabs
     * FIXME: Once fix is available, update vuetify version
     */
    ensureWindow() {
      clearTimeout(this.windowTimeout)
      this.windowTimeout = setTimeout(() => {
        this.$refs.window.internalHeight = null
        this.$refs.window.isActive = false
      }, 1000)
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     * */
    XVATRulesService.$on("event:ruleCollectionInfoValidation", this.validateRuleCollectionInfo)
    XVATRulesService.$on("event:ruleCollectionRuleSetsValidation", this.validateRuleCollectionRuleSets)
    XVATRulesService.$on("event:validateForUpdateRuleCollection", () => {
      if (this.isEditMode) {
        this.validateForUpdateRuleCollection()
      }
    })
    AppService.$on("event:preIngestServerError", () => {
      this.showSnackBar("error", this.messages.error.SERVER_ERROR)
    })
  },

  /**
   * Triggered on first entry in to the route /xvat/rule-set
   * @param {object} to
   * @param {object} from
   * @param {Function} next
   */
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.loadRuleCollectionData(to.params.id)
    })
  },

  /**
   * Triggered on update to the route /xvat/rule-set
   * @param {object} to
   * @param {object} from
   * @param {Function} next
   */
  beforeRouteUpdate(to, from, next) {
    this.loadRuleCollectionData(to.params.id)
      .then(() => {
        next()
      })
  },
  beforeDestroy() {
    XVATRulesService.$off("event:ruleCollectionInfoValidation", this.validateRuleCollectionInfo)
    XVATRulesService.$off("event:ruleCollectionRuleSetsValidation", this.validateRuleCollectionRuleSets)
    XVATRulesService.$off("event:validateForUpdateRuleCollection")
  }
}
</script>

<style scoped>
.manage-rulecollection .tabs-extension {
  height: 48px;
}

.manage-rulecollection {
  min-height: 90%;
}
</style>



// WEBPACK FOOTER //
// src/XVAT/RuleCollection/XVATManageRuleCollection.vue